import * as R from 'remeda';
import { Queue } from '@rossum/api-client/queues';
import { AnnotationStatus } from '@rossum/api-client/shared';
import {
  IconClock,
  IconCustomBoltRestore,
  IconCustomBorderCornersRestore,
  IconCustomExport,
  IconCustomExportRestore,
  IconCustomEyeReview,
  IconCustomFolderMove,
  IconEye,
  IconHierarchy,
  IconHistoryToggle,
  IconLink,
  IconLogout,
  IconRefresh,
  IconSchool,
  IconTag,
  IconTrash,
  IconTrashX,
  TablerIcon,
} from '@rossum/ui/icons/tabler';
import { LocalizationKeys } from '../../../i18n';
import {
  AnnotationAction,
  isDeletable,
  isPostponable,
  isPurgeable,
  isReAutomatable,
  isReExtractable,
  isReprocessable,
  isReviewable,
} from '../../../redux/modules/annotation/helpers';

export type SelectionPanelButtonAction = Exclude<
  AnnotationAction,
  | 'upload'
  | 'addAttachment'
  | 'createRelation'
  | 'toggleTraining'
  | 'documentHistory'
>;

type PanelButtonMeta = {
  localization: LocalizationKeys;
  dataCy: string;
  isPrimaryAction: boolean;
};

type ActionParamsFromQueue = Pick<Queue, 'useConfirmedState' | 'url'>;
// TODO mission-simplified-dashboard
// not sure whether this shouldn't be memoized in some way
export const getSupportedBatchActions = <
  T extends {
    status: AnnotationStatus;
    queue: string;
    restricted_access?: boolean;
  },
>(
  annotations: Array<T>,
  actionParamsFromQueue: Array<ActionParamsFromQueue>,
  labelsEnabled: boolean,
  canUserPurge: boolean
) => {
  const useConfirmedState = R.fromEntries(
    actionParamsFromQueue.map(({ url, useConfirmedState }) => [
      url,
      useConfirmedState ?? false,
    ])
  );

  const actions: Record<SelectionPanelButtonAction, boolean | 'disabled'> = {
    export: annotations.some(({ restricted_access }) => restricted_access)
      ? 'disabled'
      : true,
    validate: annotations.every(
      ({ status, restricted_access }) =>
        isReviewable(status) && !restricted_access
    ),
    postpone: annotations.every(
      ({ status, restricted_access }) =>
        isPostponable(status) && !restricted_access
    ),
    reprocess: annotations.every(
      ({ status, queue, restricted_access }) =>
        isReprocessable(status) &&
        !useConfirmedState[queue] &&
        !restricted_access
    ),
    reprocessConfirmed: annotations.every(
      ({ status, queue, restricted_access }) =>
        isReprocessable(status) &&
        useConfirmedState[queue] &&
        !restricted_access
    ),
    move: true,
    purge:
      annotations.every(({ status }) => isPurgeable(status)) && canUserPurge,
    delete: annotations.every(({ status }) => isDeletable(status)),
    reExtract: annotations.every(
      ({ status, restricted_access }) =>
        isReExtractable({ status }) && !restricted_access
    ),
    reAutomate: annotations.every(
      ({ status, restricted_access }) =>
        isReAutomatable({ status }) && !restricted_access
    ),
    label:
      annotations.every(({ restricted_access }) => !restricted_access) &&
      labelsEnabled,
    moveToReview: annotations.every(
      ({ restricted_access }) => !restricted_access
    ),
  };

  return actions;
};

export const panelButtonMeta: Record<
  SelectionPanelButtonAction,
  PanelButtonMeta
> = {
  validate: {
    localization: 'containers.annotationList.selectionPanel.buttons.review',
    dataCy: 'review-eye-icon',
    isPrimaryAction: true,
  },
  reprocess: {
    localization: 'components.annotationOverview.actionButtons.reprocessStatus',
    dataCy: 'reprocess-refresh-icon',
    isPrimaryAction: false,
  },
  reprocessConfirmed: {
    localization:
      'components.annotationOverview.actionButtons.reprocessConfirmedStatus',
    dataCy: 'reprocess-refresh-icon',
    isPrimaryAction: false,
  },
  postpone: {
    localization: 'containers.annotationList.selectionPanel.buttons.postpone',
    dataCy: 'postpone-clock-icon',
    isPrimaryAction: false,
  },
  move: {
    localization: 'components.annotationOverview.actionButtons.move',
    dataCy: 'move-to-icon',
    isPrimaryAction: false,
  },
  reExtract: {
    localization: 'containers.annotationList.selectionPanel.buttons.reExtract',
    dataCy: 'batch-re-extract-button',
    isPrimaryAction: false,
  },
  reAutomate: {
    localization: 'containers.annotationList.selectionPanel.buttons.reAutomate',
    dataCy: 'batch-re-automate-button',
    isPrimaryAction: false,
  },
  delete: {
    localization: 'containers.annotationList.selectionPanel.buttons.delete',
    dataCy: 'batch-delete-button',
    isPrimaryAction: false,
  },
  purge: {
    localization: 'containers.annotationList.selectionPanel.buttons.purge',
    dataCy: 'batch-purge-button',
    isPrimaryAction: false,
  },
  label: {
    localization: 'containers.annotationList.selectionPanel.buttons.label',
    dataCy: 'batch-label-button',
    isPrimaryAction: false,
  },
  export: {
    localization: 'containers.annotationList.selectionPanel.buttons.export',
    dataCy: 'export-all-icon',
    isPrimaryAction: false,
  },
  moveToReview: {
    localization:
      'containers.annotationList.selectionPanel.buttons.moveToReview',
    dataCy: 'move-to-review-icon',
    isPrimaryAction: true,
  },
};

export const actionIcons: Record<AnnotationAction, TablerIcon> = {
  export: IconCustomExport,
  upload: IconLogout,
  label: IconTag,
  validate: IconEye,
  reprocess: IconRefresh,
  reprocessConfirmed: IconRefresh,
  postpone: IconClock,
  move: IconCustomFolderMove,
  delete: IconTrash,
  purge: IconTrashX,
  reExtract: IconCustomBorderCornersRestore,
  reAutomate: IconCustomBoltRestore,
  addAttachment: IconLink,
  createRelation: IconHierarchy,
  toggleTraining: IconSchool,
  moveToReview: IconCustomEyeReview,
  documentHistory: IconHistoryToggle,
};

export type ExportMutationContext = 'confirmed' | 'failed_export';

export const exportMutationIcons: Record<ExportMutationContext, TablerIcon> = {
  confirmed: IconCustomExport,
  failed_export: IconCustomExportRestore,
};

export type ExportMutationIneligibilityReason =
  | 'mixed_status'
  | 'not_exportable_status'
  | 'restricted_access'
  | 'hide_export_button';

export type ExportMutationIneligibilityCount = {
  reason: ExportMutationIneligibilityReason;
  count: number;
};

export type ExportMutationState =
  | { visible: false }
  | {
      visible: true;
      context: ExportMutationContext;
      disabled: boolean;
      totalCount: number;
      ineligibleCount: number;
      ineligible: ReadonlyArray<ExportMutationIneligibilityCount>;
    };

type ExportableAnnotation = {
  status: AnnotationStatus;
  queue: string;
  restricted_access?: boolean;
};

/**
 * Highest-priority blocking reason wins, so each ineligible annotation is
 * counted exactly once in the resulting tooltip.
 */
const classifyIneligibility = (
  annotation: ExportableAnnotation,
  context: ExportMutationContext,
  showExportButton: Record<string, boolean>
): ExportMutationIneligibilityReason | null => {
  if (annotation.status === context) {
    if (annotation.restricted_access) return 'restricted_access';
    if (!showExportButton[annotation.queue]) return 'hide_export_button';
    return null;
  }

  if (
    annotation.status === 'confirmed' ||
    annotation.status === 'failed_export'
  )
    return 'mixed_status';

  return 'not_exportable_status';
};

const isEligibleFor = (
  annotation: ExportableAnnotation,
  context: ExportMutationContext,
  showExportButton: Record<string, boolean>
) =>
  annotation.status === context &&
  !annotation.restricted_access &&
  showExportButton[annotation.queue];

const aggregateReasons = (
  reasons: ReadonlyArray<ExportMutationIneligibilityReason>
): ReadonlyArray<ExportMutationIneligibilityCount> => {
  const order: ReadonlyArray<ExportMutationIneligibilityReason> = [
    'mixed_status',
    'not_exportable_status',
    'restricted_access',
    'hide_export_button',
  ];

  return order.flatMap(reason => {
    const count = reasons.filter(r => r === reason).length;
    return count > 0 ? [{ reason, count }] : [];
  });
};

/**
 * Computes whether the export-mutation menu row should be shown, what label
 * (context) it carries, and — when disabled — the aggregated reasons why some
 * selected annotations block the action.
 *
 * Visibility rules:
 * - Hidden when no selected annotation is eligible for either `confirmed` or
 *   `failed_export` mutation.
 * - Visible when at least one annotation is eligible for at least one context.
 *
 * Context (label) rules:
 * - `failed_export` only when every eligible annotation is `failed_export`.
 * - `confirmed` otherwise (covers all-confirmed and mixed eligible selections).
 *
 * Disabled rule:
 * - Disabled whenever any selected annotation is not eligible for the chosen
 *   context (mixed status, restricted access, or `hideExportButton` queue).
 */
export const getExportMutationState = <T extends ExportableAnnotation>(
  annotations: Array<T>,
  showExportButton: Record<string, boolean>
): ExportMutationState => {
  if (annotations.length === 0) return { visible: false };

  const eligibleConfirmed = annotations.filter(a =>
    isEligibleFor(a, 'confirmed', showExportButton)
  );
  const eligibleFailedExport = annotations.filter(a =>
    isEligibleFor(a, 'failed_export', showExportButton)
  );

  if (eligibleConfirmed.length === 0 && eligibleFailedExport.length === 0)
    return { visible: false };

  const context: ExportMutationContext =
    eligibleConfirmed.length === 0 ? 'failed_export' : 'confirmed';

  const ineligibleReasons = annotations.flatMap(a => {
    const reason = classifyIneligibility(a, context, showExportButton);
    return reason ? [reason] : [];
  });

  return {
    visible: true,
    context,
    disabled: ineligibleReasons.length > 0,
    totalCount: annotations.length,
    ineligibleCount: ineligibleReasons.length,
    ineligible: aggregateReasons(ineligibleReasons),
  };
};
